import React, { useState, useEffect, useContext, useRef } from "react";
import { createPortal } from "react-dom";
import { FaUserTie, FaCalendarAlt, FaClock, FaChevronDown } from "react-icons/fa";
import { HiOutlineLink } from "react-icons/hi";
import { GrYoga } from "react-icons/gr";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { Loader } from "lucide-react";
import Context from "../../../../Context/Context";

// Custom Dropdown Component
const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  disabled = false,
  required = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        portalRef.current && !portalRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      // Always position below the button
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width,
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || null;
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const clickableOptions = options.filter(opt => !opt.disabled);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-11 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
          !selectedOption ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : ''
        }`}
      >
        <span className="truncate">{displayValue}</span>
        <FaChevronDown 
          className={`ml-2 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={portalRef}
          className={`fixed z-[99999] rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="max-h-60 overflow-y-auto">
            {clickableOptions.length === 0 ? (
              <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No options available
              </div>
            ) : (
              clickableOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange({ target: { value: option.value } });
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left transition-colors duration-150 ${
                    value === option.value
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                        : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                      : isDarkMode
                      ? 'text-white hover:bg-[#2a2a2a]'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Custom Date Picker Component
const CustomDatePicker = ({ 
  value, 
  onChange, 
  isDarkMode, 
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const datePickerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      // Always position below the button
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 320 - 8)),
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      setCurrentMonth(new Date(value));
    }
  }, [value]);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "Select date...";
    const date = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${date.getDate()} ${months[date.getMonth()]}, ${date.getFullYear()}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const handleDateSelect = (day) => {
    if (day === null) return;
    // Create date in local timezone and format as YYYY-MM-DD
    const year = currentMonth.getFullYear();
    const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    const formattedDate = `${year}-${month}-${dayStr}`;
    onChange({ target: { value: formattedDate } });
    setIsOpen(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1));
  };

  const today = new Date();
  const selectedDate = value ? new Date(value) : null;
  const days = getDaysInMonth(currentMonth);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="relative" ref={datePickerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-11 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
          !value ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : ''
        }`}
      >
        <span className="truncate">{formatDisplayDate(value)}</span>
        <FaCalendarAlt 
          className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={dropdownRef}
          className={`fixed z-[99999] w-[calc(100vw-2rem)] md:w-80 rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            maxWidth: '320px',
          }}
        >
          <div className="p-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => navigateMonth(-1)}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button
                type="button"
                onClick={() => navigateMonth(1)}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-[#2a2a2a] text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Day Names */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map((day) => (
                <div
                  key={day}
                  className={`text-xs font-medium text-center py-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="aspect-square" />;
                }

                const isToday = 
                  day === today.getDate() &&
                  currentMonth.getMonth() === today.getMonth() &&
                  currentMonth.getFullYear() === today.getFullYear();

                const isSelected = 
                  selectedDate &&
                  day === selectedDate.getDate() &&
                  currentMonth.getMonth() === selectedDate.getMonth() &&
                  currentMonth.getFullYear() === selectedDate.getFullYear();

                const isPast = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => !isPast && handleDateSelect(day)}
                    disabled={isPast}
                    className={`aspect-square rounded-lg text-sm font-medium transition-all duration-150 ${
                      isSelected
                        ? 'bg-[#FDCF08] text-black font-semibold'
                        : isPast
                        ? isDarkMode
                          ? 'text-gray-600 cursor-not-allowed'
                          : 'text-gray-300 cursor-not-allowed'
                        : isDarkMode
                        ? 'text-white hover:bg-[#2a2a2a]'
                        : 'text-gray-900 hover:bg-gray-100'
                    } ${isToday && !isSelected ? (isDarkMode ? 'ring-1 ring-[#FDCF08]' : 'ring-1 ring-[#FDCF08]') : ''}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Custom Time Picker Component
const CustomTimePicker = ({ 
  value, 
  onChange, 
  isDarkMode, 
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const timePickerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      // Always position below the button
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 256 - 8)),
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Parse time value to get hours, minutes, and AM/PM
  const parseTime = (timeString) => {
    if (!timeString) return { hour: 12, minute: 0, ampm: 'AM' };
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 === 0 ? 12 : (hour24 > 12 ? hour24 - 12 : hour24);
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return { hour: hour12, minute: parseInt(minutes, 10), ampm };
  };

  // Convert 12-hour format to 24-hour format
  const convertTo24Hour = (hour12, minute, ampm) => {
    let hour24 = hour12;
    if (ampm === 'PM' && hour12 !== 12) {
      hour24 = hour12 + 12;
    } else if (ampm === 'AM' && hour12 === 12) {
      hour24 = 0;
    }
    return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const currentTime = parseTime(value);
  const [selectedHour, setSelectedHour] = useState(currentTime.hour);
  const [selectedMinute, setSelectedMinute] = useState(currentTime.minute);
  const [selectedAmPm, setSelectedAmPm] = useState(currentTime.ampm);

  useEffect(() => {
    const newTime = parseTime(value);
    setSelectedHour(newTime.hour);
    setSelectedMinute(newTime.minute);
    setSelectedAmPm(newTime.ampm);
  }, [value]);

  const handleTimeChange = (hour, minute, ampm) => {
    const time24 = convertTo24Hour(hour, minute, ampm);
    onChange({ target: { value: time24 } });
  };

  const formatDisplayTime = (timeString) => {
    if (!timeString) return "Select time...";
    const { hour, minute, ampm } = parseTime(timeString);
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return (
    <div className="relative" ref={timePickerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-11 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${
          !value ? (isDarkMode ? 'text-gray-400' : 'text-gray-500') : ''
        }`}
      >
        <span className="truncate">{formatDisplayTime(value)}</span>
        <FaClock 
          className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={dropdownRef}
          className={`fixed z-[99999] w-[calc(100vw-2rem)] md:w-64 rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            maxWidth: '256px',
          }}
        >
          <div className="p-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              {/* Hours */}
              <div className="flex-1">
                <div className={`text-xs font-medium mb-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Hour
                </div>
                <div className={`max-h-48 overflow-y-auto rounded-lg border ${
                  isDarkMode ? 'border-[#2a2a2a] bg-[#0f0f0f]' : 'border-gray-200 bg-gray-50'
                }`}>
                  {hours.map((hour) => (
                    <button
                      key={hour}
                      type="button"
                      onClick={() => {
                        setSelectedHour(hour);
                        handleTimeChange(hour, selectedMinute, selectedAmPm);
                      }}
                      className={`w-full px-3 py-2 text-sm transition-colors duration-150 ${
                        selectedHour === hour
                          ? isDarkMode
                            ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                            : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                          : isDarkMode
                          ? 'text-white hover:bg-[#2a2a2a]'
                          : 'text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {hour}
                    </button>
                  ))}
                </div>
              </div>

              {/* Minutes */}
              <div className="flex-1">
                <div className={`text-xs font-medium mb-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Minute
                </div>
                <div className={`max-h-48 overflow-y-auto rounded-lg border ${
                  isDarkMode ? 'border-[#2a2a2a] bg-[#0f0f0f]' : 'border-gray-200 bg-gray-50'
                }`}>
                  {minutes.map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      onClick={() => {
                        setSelectedMinute(minute);
                        handleTimeChange(selectedHour, minute, selectedAmPm);
                      }}
                      className={`w-full px-3 py-2 text-sm transition-colors duration-150 ${
                        selectedMinute === minute
                          ? isDarkMode
                            ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                            : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                          : isDarkMode
                          ? 'text-white hover:bg-[#2a2a2a]'
                          : 'text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {minute.toString().padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>

              {/* AM/PM */}
              <div className="flex-1">
                <div className={`text-xs font-medium mb-2 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Period
                </div>
                <div className={`max-h-48 overflow-y-auto rounded-lg border ${
                  isDarkMode ? 'border-[#2a2a2a] bg-[#0f0f0f]' : 'border-gray-200 bg-gray-50'
                }`}>
                  {['AM', 'PM'].map((period) => (
                    <button
                      key={period}
                      type="button"
                      onClick={() => {
                        setSelectedAmPm(period);
                        handleTimeChange(selectedHour, selectedMinute, period);
                      }}
                      className={`w-full px-3 py-2 text-sm transition-colors duration-150 ${
                        selectedAmPm === period
                          ? isDarkMode
                            ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                            : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                          : isDarkMode
                          ? 'text-white hover:bg-[#2a2a2a]'
                          : 'text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                isDarkMode
                  ? 'bg-[#2a2a2a] text-white hover:bg-[#3a3a3a]'
                  : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
              }`}
            >
              Done
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const ClassModal = ({
  isOpen,
  onClose,
  // institutionData,
  classTypeNameArray,
  instructorList,
  userData,
  setUpcomingClasses,
  upcomingClasses,
  util,
  classData = null, // null for create, object for edit
  readOnly = false,
  setModalMode,
}) => {
  const Ctx = useContext(Context);
  const isDarkMode = Ctx.isDarkMode ?? true;
  const isEditMode = !!classData;
  const [loading, setLoading] = useState(false);
  const [classType, setClassType] = useState(classData?.classType || "");
  const [zoomLink, setZoomLink] = useState(classData?.zoomLink || "");
  
  // Get the actual instructor list (handle wrapped responses)
  const getActualInstructorList = () => {
    if (!instructorList) {
      console.log("InstructorList is null/undefined");
      return [];
    }
    if (Array.isArray(instructorList)) {
      console.log("InstructorList is already an array with length:", instructorList.length);
      return instructorList;
    }
    // Handle string body (AWS Lambda response)
    if (instructorList.body) {
      try {
        const parsed = typeof instructorList.body === 'string' ? JSON.parse(instructorList.body) : instructorList.body;
        if (Array.isArray(parsed)) {
          console.log("Found array in body:", parsed);
          return parsed;
        }
      } catch (e) {
        console.error("Error parsing body:", e);
      }
    }
    if (instructorList.data && Array.isArray(instructorList.data)) {
      console.log("Found array in data:", instructorList.data);
      return instructorList.data;
    }
    if (instructorList.instructors && Array.isArray(instructorList.instructors)) {
      console.log("Found array in instructors:", instructorList.instructors);
      return instructorList.instructors;
    }
    console.warn("InstructorList is not in expected format:", instructorList);
    return [];
  };

  // Set default instructor to Anupam when creating new class
  const getDefaultInstructor = () => {
    if (classData) {
      return {
        name: classData.instructorNames,
        instructorId: classData.instructorId,
        emailId: classData.instructorEmailId,
      };
    }
    // Find Anupam in the instructor list
    const actualList = getActualInstructorList();
    if (actualList && actualList.length > 0) {
      const anupam = actualList.find(
        (i) => i.name?.toLowerCase().includes("anupam") || i.name?.toLowerCase() === "anupam"
      );
      if (anupam) {
        return {
          name: anupam.name,
          instructorId: anupam.instructorId,
          emailId: anupam.emailId,
        };
      }
      // If Anupam not found, use first instructor
      return {
        name: actualList[0].name,
        instructorId: actualList[0].instructorId,
        emailId: actualList[0].emailId,
      };
    }
    return {};
  };

  const [selectedInstructor, setSelectedInstructor] = useState(getDefaultInstructor());
  const [datePicker, setDatePicker] = useState(
    classData?.date
      ? new Date(parseInt(classData.date)).toISOString().split("T")[0]
      : ""
  );
  const [time, setTime] = useState(
    classData?.date
      ? new Date(parseInt(classData.date)).toTimeString().slice(0, 5)
      : "12:00"
  );

  useEffect(() => {
    if (classData) {
      setClassType(classData.classType || "");
      setZoomLink(classData.zoomLink || "");
      setSelectedInstructor({
        name: classData.instructorNames,
        instructorId: classData.instructorId,
        emailId: classData.instructorEmailId,
      });
      setDatePicker(
        classData.date
          ? new Date(parseInt(classData.date)).toLocaleDateString("sv-SE")
          : ""
      );
      setTime(
        classData.date
          ? new Date(parseInt(classData.date))
              .toTimeString()
              .slice(0, 5)
          : "12:00"
      );
    } else {
      // Reset to default when creating new class (set Anupam as default)
      const defaultInstructor = getDefaultInstructor();
      if (defaultInstructor.name) {
        setSelectedInstructor(defaultInstructor);
      }
    }
  }, [classData, instructorList]);

  const getInstructor = (name) => {
    const actualList = getActualInstructorList();
    if (!actualList || actualList.length === 0 || !name || name === "none") {
      return null;
    }
    const instructor = actualList.find(
      (i) => i.name?.toString().trim() === name?.toString().trim()
    );
    if (!instructor) {
      console.warn("Instructor not found:", name, "Available instructors:", actualList);
    }
    return instructor || null;
  };

  // Debug: Log instructorList when modal opens and handle wrapped responses
  useEffect(() => {
    if (isOpen) {
      console.log("Raw Instructor List:", instructorList);
      console.log("Instructor List Type:", typeof instructorList, Array.isArray(instructorList));
      
      // Check if response is wrapped
      let actualInstructorList = instructorList;
      if (instructorList && !Array.isArray(instructorList)) {
        if (instructorList.data && Array.isArray(instructorList.data)) {
          actualInstructorList = instructorList.data;
          console.log("Found wrapped response in .data");
        } else if (instructorList.body && Array.isArray(instructorList.body)) {
          actualInstructorList = instructorList.body;
          console.log("Found wrapped response in .body");
        } else if (instructorList.instructors && Array.isArray(instructorList.instructors)) {
          actualInstructorList = instructorList.instructors;
          console.log("Found wrapped response in .instructors");
        }
      }
      
      if (actualInstructorList && actualInstructorList.length > 0) {
        console.log("Processed Instructor List:", actualInstructorList);
        console.log("First Instructor:", actualInstructorList[0]);
        console.log("Instructor keys:", Object.keys(actualInstructorList[0]));
      } else {
        console.warn("Instructor list is empty or not an array");
      }
    }
  }, [isOpen, instructorList]);

  function canCreateClass(filteredClasses, newClass) {
    return !filteredClasses.some(
      (existingClass) =>
        existingClass.classType === newClass.classType &&
        existingClass.instructorId === newClass.instructorId &&
        existingClass.instructorNames === newClass.instructorNames &&
        existingClass.date === newClass.date &&
        existingClass.zoomLink === newClass.zoomLink
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!classType || !selectedInstructor.name || !datePicker || !time) {
        toast.warn("Please fill in all sections.");
        return;
      }

      const newClass = {
        classType: classType,
        instructorId: selectedInstructor.instructorId,
        instructorNames: selectedInstructor.name,
        date: new Date(datePicker + "T" + time).getTime(),
      };

      if (canCreateClass(upcomingClasses, newClass)) {
        console.log("✅ You can create the class");
      } else {
        // console.log(
        //   "❌ Class already exists with same type, instructor, and date"
        // );
        toast.warn("Same schedule already exist!");
        return;
      }
      if (zoomLink) {
        try {
          new URL(zoomLink);
        } catch (error) {
          toast.warn("Invalid Zoom link. Please enter a valid URL.");
          return;
        }
      }

      // Ensure we have all required instructor fields
      if (!selectedInstructor.emailId) {
        console.warn("Selected instructor missing emailId:", selectedInstructor);
        // Try to get the full instructor object from the list
        const fullInstructor = getInstructor(selectedInstructor.name);
        if (fullInstructor) {
          selectedInstructor.emailId = fullInstructor.emailId;
          selectedInstructor.instructorId = fullInstructor.instructorId || selectedInstructor.instructorId;
        }
      }

      const classPayload = {
        classType,
        startTimeEst: new Date(datePicker + "T" + time).getTime(),
        instructorEmailId: selectedInstructor.emailId,
        duration: 60,
        instructorId: selectedInstructor.instructorId,
        instructorNames: selectedInstructor.name,
        classDescription: "",
        zoomLink: zoomLink || "",
        date: new Date(datePicker + "T" + time).getTime(),
      };

      if (isEditMode) {
        // Edit existing class
        await API.put("user", `/admin/edit-schedule-name/Bworkz`, {
          body: {
            classId: classData.classId,
            ...classPayload,
          },
        });
        const updatedClasses = upcomingClasses.map((c) =>
          c.classId === classData.classId
            ? { ...c, ...classPayload, classId: classData.classId }
            : c
        );
        setUpcomingClasses(updatedClasses);
        toast.success("Class Updated");
      } else {
        // Create new class
        const newClass = await API.post("user", `/admin/add-schedule/Bworkz`, {
          body: classPayload,
        });
        toast.success("Class Added");
        setUpcomingClasses([...upcomingClasses, newClass]);
      }
      handleClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatForInput = (epochDate) => {
    if (!epochDate) return "";
    const date = new Date(epochDate);
    return date.toLocaleDateString("sv-SE"); // ✅ Proper "YYYY-MM-DD" for input
  };

  const handleClose = () => {
    setClassType("");
    setSelectedInstructor({});
    setZoomLink("");
    setDatePicker("");
    setTime("00:00");
    setModalMode("");
    onClose();
  };

  if (!isOpen) return null;

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Format time for display
  const formatDisplayTime = (timeString) => {
    if (!timeString) return "";
    return timeString;
  };

  return (
    <>
      <div 
        className={`fixed inset-0 z-[100] flex items-center justify-center ${isDarkMode ? 'bg-black/60' : 'bg-black/20'} backdrop-blur-md transition-opacity duration-300 p-2 md:p-4`} 
        key={`modal-${isDarkMode}`}
        onClick={handleClose}
      >
      <div 
        className={`${isDarkMode ? 'bg-[#0f0f0f]' : 'bg-white'} rounded-xl md:rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] md:max-h-[90vh] flex flex-col transition-all duration-300 transform scale-100`} 
        style={{ fontFamily: "Inter, sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with NEW SESSION tag */}
          <div className={`px-3 md:px-6 py-2.5 md:py-3 flex-shrink-0`}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col items-start gap-1.5 md:gap-3 flex-1">
              {!readOnly && !isEditMode && (
                <span className="bg-[#FDCF08] text-black text-[10px] md:text-xs font-[600] tracking-wider px-2 md:px-3 py-0.5 md:py-1 rounded-full uppercase">
                  NEW SESSION
                </span>
              )}
              {!readOnly && isEditMode && (
                <span className="bg-[#FDCF08] text-black text-[10px] md:text-xs font-[600] tracking-wider px-2 md:px-3 py-0.5 md:py-1 rounded-full uppercase">
                  EDIT SESSION
                </span>
              )}
              
              <h2 className={`text-base md:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {readOnly
                  ? "View Class"
                  : isEditMode
                  ? "Edit Class"
                  : "Create Class"}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className={`w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center transition-all duration-200 flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-[#1a1a1a]' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'} focus:outline-none`}
            >
              <svg
                className="w-4 h-4 md:w-5 md:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          {!readOnly && !isEditMode && (
            <p className={`text-[11px] md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1 md:mt-2`}>
              Set up your next Bworkz session with time, instructor and <span className="hidden md:inline"><br /></span>Zoom link.
            </p>
          )}
          {!readOnly && isEditMode && (
            <p className={`text-[11px] md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-1 md:mt-2`}>
              Update your Bworkz session details including time, instructor <span className="hidden md:inline"><br /></span>and Zoom link.
            </p>
          )}
        </div>

        {/* Scrollable Body */}
        <form onSubmit={onSubmit} className="flex-1 overflow-y-auto px-3 md:px-6 pb-3 md:pb-4 space-y-3 md:space-y-5">
          {/* Class Type */}
          <div className="w-full space-y-1.5 md:space-y-2">
            <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
              Class Type
            </label>
            <CustomDropdown
              value={classType}
              onChange={(e) => setClassType(e.target.value)}
              options={[
                { value: "", label: "Select class style...", disabled: true },
                ...(isEditMode
                  ? classTypeNameArray
                  : classTypeNameArray.slice(0, -1)
                ).map((type) => ({ value: type, label: type }))
              ]}
              placeholder="Select class style..."
              isDarkMode={isDarkMode}
              disabled={readOnly}
              required
            />
          </div>

          {/* Instructor */}
          <div className="w-full space-y-1.5 md:space-y-2">
            <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
              Instructor
            </label>
            <CustomDropdown
              value={selectedInstructor.name || ""}
              onChange={(e) => {
                const instructor = getInstructor(e.target.value);
                if (instructor) {
                  setSelectedInstructor(instructor);
                } else {
                  console.warn("Could not find instructor:", e.target.value);
                }
              }}
              options={(() => {
                const actualList = getActualInstructorList();
                if (actualList && actualList.length > 0) {
                  return [
                    { value: "", label: "Select instructor...", disabled: true },
                    ...actualList.map((i) => {
                      const instructorName = i.name || i.userName || "Unknown";
                      return { value: instructorName, label: instructorName };
                    })
                  ];
                } else {
                  return [{ value: "", label: "No instructors available", disabled: true }];
                }
              })()}
              placeholder="Select instructor..."
              isDarkMode={isDarkMode}
              disabled={readOnly}
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {/* Date */}
            <div className="w-full space-y-1.5 md:space-y-2">
              <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Date
              </label>
              <CustomDatePicker
                value={datePicker}
                onChange={(e) => setDatePicker(e.target.value)}
                isDarkMode={isDarkMode}
                disabled={readOnly}
              />
            </div>
            {/* Time */}
            <div className="w-full space-y-1.5 md:space-y-2">
              <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                Time
              </label>
              <CustomTimePicker
                value={time}
                onChange={(e) => setTime(e.target.value)}
                isDarkMode={isDarkMode}
                disabled={readOnly}
              />
            </div>
          </div>
          {/* Zoom Link */}
          <div className="space-y-1.5 md:space-y-2">
            <label className={`block text-xs md:text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
              Zoom Link
            </label>
            <div className="relative">
              <input
                type="text"
                value={zoomLink}
                onChange={(e) => setZoomLink(e.target.value)}
                placeholder="https://zoom.us/j/..."
                className={`border-none w-full h-9 md:h-11 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-colors ${isDarkMode ? 'bg-[#1a1a1a] text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' : 'bg-white border text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'}`}
                style={isDarkMode ? { backgroundColor: '#1a1a1a' } : {}}
                disabled={readOnly}
              />
              <span className={`absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <HiOutlineLink size={18} />
              </span>
            </div>
          </div>
        </form>
          
        {/* Footer: Only show submit if not readOnly */}
        {!readOnly && (
          <div className={`px-3 md:px-6 py-2.5 md:py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 border-t flex-shrink-0 ${isDarkMode ? 'border-[#1a1a1a]' : 'border-gray-100'}`}>
            <button
              type="button"
              onClick={handleClose}
              className={`h-9 md:h-11 px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-medium text-xs md:text-sm transition-all duration-200 order-2 sm:order-1 ${isDarkMode ? 'bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={loading}
              className="h-9 md:h-11 px-3 bg-[#FDCF08] text-black font-semibold rounded-lg flex items-center justify-center gap-1.5 md:gap-2 text-xs md:text-sm hover:bg-[#f0c000] focus:outline-none focus:ring-2 focus:ring-[#FDCF08] focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-200 order-1 sm:order-2"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  {isEditMode ? "Saving..." : "Creating..."}
                </>
              ) : (
                <>
                  {isEditMode ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Save Changes
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Create Class
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ClassModal;
